// Tuile d'indicateur : valeur, fraîcheur, états écrits (« non activé », « non renseigné »,
// service non disponible), lien vers la liste explicative avec les mêmes filtres.
import { Lien, Squelette } from '@cc/ui';
import { ArrowDown, ArrowUp, EyeOff } from 'lucide-react';
import { ErreurApi } from '../../api/erreurs';
import { fraicheur } from '../../commun/format';
import type { DefinitionTuile } from './catalogue';
import { libelleUnite, useListeSocle, useValeurIndicateur, type FiltresTableau } from './donnees';
import styles from './tableau.module.css';

export interface ProprietesTuile {
  definition: DefinitionTuile;
  filtres: FiltresTableau;
  /** Modules actifs (null si inconnus pour cet utilisateur). */
  modulesActifs: Set<string> | null;
  edition?: { onMonter?: () => void; onDescendre?: () => void; onMasquer: () => void };
}

export function hrefListe(code: string, filtres: FiltresTableau): string {
  const p = new URLSearchParams({ periode: filtres.periode });
  if (filtres.arrondissement) p.set('arrondissement', filtres.arrondissement);
  return `/tableau-de-bord/liste/${code}?${p.toString()}`;
}

function Etat({ texte, detail }: { texte: string; detail?: string }) {
  return (
    <>
      <p className={styles.etatTuile}>{texte}</p>
      {detail ? <p className={styles.fraicheur}>{detail}</p> : null}
    </>
  );
}

function Contenu({ definition, filtres, modulesActifs }: Omit<ProprietesTuile, 'edition'>) {
  const moduleInactif = Boolean(definition.module && modulesActifs && !modulesActifs.has(definition.module));
  const valeur = useValeurIndicateur(definition, filtres, !moduleInactif);
  const socle = useListeSocle(definition.code, definition.source === 'socle');

  if (moduleInactif) return <Etat texte="Module non activé" detail={`Le module ${definition.module} est désactivé dans cette collectivité.`} />;

  if (definition.source === 'socle') {
    if (socle.chargement) return <Squelette hauteurBloc={40} libelle={`Calcul de ${definition.libelle}`} />;
    if (socle.erreur || socle.lignes === null) return <Etat texte="Valeur indisponible" detail="La source n'a pas répondu." />;
    return (
      <>
        <p className={styles.valeur} data-testid={`valeur-${definition.code}`}>
          {socle.lignes.length.toLocaleString('fr-FR')}
        </p>
        <p className={styles.fraicheur}>Calculé à l&apos;instant à partir des données enregistrées</p>
      </>
    );
  }

  if (valeur.isPending) return <Squelette hauteurBloc={40} libelle={`Calcul de ${definition.libelle}`} />;
  if (valeur.error) {
    if (valeur.error instanceof ErreurApi && valeur.error.routeIndisponible) {
      return <Etat texte="Service des indicateurs non disponible" detail="Il sera actif après le déploiement du lot L0-C sur ce serveur." />;
    }
    return <Etat texte="Valeur indisponible" detail={valeur.error instanceof Error ? valeur.error.message : undefined} />;
  }
  const v = valeur.data;
  if (v.couverture.etat === 'non_active') return <Etat texte="Module non activé" detail={`Le module ${v.couverture.module} est désactivé dans cette collectivité.`} />;
  if (v.couverture.etat === 'non_branche') return <Etat texte="Valeur indisponible" detail={v.couverture.message ?? undefined} />;
  if (v.valeur === null || v.couverture.etat === 'non_renseigne') return <Etat texte="Non renseigné" detail="Aucune opération enregistrée pour cette période." />;
  const ancien = Date.now() - new Date(v.calcule_le).getTime() > 24 * 3600 * 1000;
  return (
    <>
      <p className={styles.valeur} data-testid={`valeur-${definition.code}`}>
        {v.valeur.toLocaleString('fr-FR')}
        {libelleUnite(v.unite) ? <span className={styles.unite}>{libelleUnite(v.unite)}</span> : null}
      </p>
      <p className={styles.fraicheur} data-ancien={ancien || undefined}>
        Calculé {fraicheur(v.calcule_le)}
        {ancien ? ' (donnée de plus de 24 heures)' : ''}
      </p>
    </>
  );
}

export function Tuile({ definition, filtres, modulesActifs, edition }: ProprietesTuile) {
  return (
    <article className={styles.tuile} data-ton={definition.ton} aria-labelledby={`tuile-${definition.code}`}>
      <header className={styles.enteteTuile}>
        <h3 id={`tuile-${definition.code}`}>{definition.libelle}</h3>
        <span className={styles.code}>{definition.code}</span>
      </header>
      <Contenu definition={definition} filtres={filtres} modulesActifs={modulesActifs} />
      <p className={styles.description}>
        {definition.description}
        {definition.instantane ? ' (situation à date)' : ''}
      </p>
      {edition ? (
        <div className={styles.edition} role="group" aria-label={`Disposition de la tuile ${definition.libelle}`}>
          <button type="button" onClick={edition.onMonter} disabled={!edition.onMonter} aria-label={`Monter ${definition.libelle}`}>
            <ArrowUp aria-hidden width={18} height={18} /> Monter
          </button>
          <button type="button" onClick={edition.onDescendre} disabled={!edition.onDescendre} aria-label={`Descendre ${definition.libelle}`}>
            <ArrowDown aria-hidden width={18} height={18} /> Descendre
          </button>
          <button type="button" onClick={edition.onMasquer} aria-label={`Masquer ${definition.libelle}`}>
            <EyeOff aria-hidden width={18} height={18} /> Masquer
          </button>
        </div>
      ) : (
        <Lien href={hrefListe(definition.code, filtres)} className={styles.ouvrir} aria-label={`Ouvrir la liste : ${definition.libelle}`}>
          Ouvrir la liste
        </Lien>
      )}
    </article>
  );
}
